import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DialogComponent } from "../../common/DialogComponent";
import { ApplicationState } from "../../../store";
import MoreScoreFactorDialog from "../../../components/Dialogs/BusinessCredMoreScoreFactorDialog";
import BCreditScoreTracker from "./BCreditScoreTracker";
import BCreditRiskTracker from "./BCreditRiskTracker";
import * as BusinessCreditReportAction from "../../../store/businessCreditReportService/actions";
import { IBusinessCreditReportResponse, BusinessCreditReportStatePII } from "../../../store/businessCreditReportService/types";
import * as services from "../../../store/service";
import DedicatedResolutionSpecialistCard from "../../RightSideCard/DedicatedResolutionSpecialistCard";
import { getFormattedDate } from "../../../util/dateFormatting";
import i18nService from "../../../util/Internationalization/i18n/i18n";
import RenderIcons from "../../../Assets/css/_icons/partner_icon/RenderIcons";
import { useTranslation } from "react-i18next";

const BCreditScore: React.FunctionComponent<{ reportObject: BusinessCreditReportStatePII }> = (props) => {
    const dispatch = useDispatch();
    const [showMoreScoreFactorDialog, setShowMoreScoreFactorDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const businessProfileStatus = useSelector(
        (state: ApplicationState) =>
            state.loggedInUser &&
            state.loggedInUser.userServiceDetails?.businessProfileStatus?.filter((obj: any) => {
                return obj.provider;
            })
    );
    const requestBusinessCreditReport = (forcefullyRefresh: boolean, creditProvider: string) =>
        dispatch(BusinessCreditReportAction.actionCreators.requestBusinessCreditReport("", forcefullyRefresh, creditProvider));
    const creditReportResponses: IBusinessCreditReportResponse[] = useSelector(
        (state: ApplicationState) => (state.businessCreditReportState && state.businessCreditReportState.creditReportData)!
    );
    const creditProvider = businessProfileStatus != null && businessProfileStatus.length > 0 ? businessProfileStatus[0].provider : "";
    const creditReport: IBusinessCreditReportResponse = useSelector(
        (state: ApplicationState) => (state.businessCreditReportState && state.businessCreditReportState.creditReportData)!
    ).find((it) => it.Provider === creditProvider)!;

    const handleMoreScoreFactorDialog = (value: string): any => {
        if (value === "show") {
            setShowMoreScoreFactorDialog(true);
        } else if (value === "close") {
            document.body.classList.remove("no_scroll");
            setShowMoreScoreFactorDialog(false);
        }
    };

    const getScore = (score: any) => {
        if (creditProvider === "DUNBRADSTREET") {
            return score === "Not Rated" ? "NR" : score === "0" ? "X" : score;
        }
        return score === "Not Rated" ? "NR" : score;
    };

    const getCreditBureauTextCssClass = () => {
        let scoreRatingText =
            creditProvider === "DUNBRADSTREET"
                ? getRatingBadgeText(props.reportObject.businessCompanyReport?.credit_score_and_limit?.current_score)
                : props.reportObject?.businessCompanyReport?.credit_score_and_limit?.current_risk_rating
                ? props.reportObject?.businessCompanyReport?.credit_score_and_limit?.current_risk_rating
                : "";
        return scoreRatingText !== null && scoreRatingText.length < 10 ? "credit-bureau-text" : "credit-bureau-text credit-bureau-long-text";
    };

    const getRatingBadgeText = (score: any) => {
        switch (score) {
            case "0":
                return "Bankrupt";
            case "1":
                return "Very Low Risk";
            case "2":
                return "Low Risk";
            case "3":
                return "Moderate Risk";
            case "4":
                return "High Risk";
            case "5":
                return "Very High Risk";
            default:
                return "Not Rated";
        }
    };

    const getRatingColor = (score: any) => {
        if (creditProvider === "DUNBRADSTREET") {
            switch (score) {
                case "0":
                    return "lvl-very-high-risk";
                case "1":
                    return "lvl-very-low-risk";
                case "2":
                    return "lvl-low-risk";
                case "3":
                    return "lvl-moderate-risk";
                case "4":
                    return "lvl-high-risk";
                case "5":
                    return "lvl-very-high-risk";
                default:
                    return "lvl-not-rated";
            }
        } else {
            return score === "Not Rated" ? "status-nr" : "status-good";
        }
    };

    const getScoreDescription = (score: any) => {
        if (creditProvider === "DUNBRADSTREET") {
            if (score === "0") {
                return "“X” denotes open bankruptcy, out of business at the location or higher risk situations";
            } else if (score === undefined) {
                return "This score represents your business probability of failure.";
            }
        }
        return `This score represents your business has a percentile of ${props.reportObject.businessCompanyReport?.credit_score_and_limit?.current_score} 
        with a ${
            creditProvider === "DUNBRADSTREET"
                ? getRatingBadgeText(props.reportObject.businessCompanyReport?.credit_score_and_limit?.current_score)
                : props.reportObject.businessCompanyReport?.credit_score_and_limit?.current_risk_rating
        } probability of failure.`;
    };

    const orderNewBusinesCreditReport = () => {
        setIsLoading(true);
        services.getNewBusinessCreditReport(null, creditProvider).then((data) => {
            requestBusinessCreditReport(true, creditProvider);
            setIsLoading(false);
        });
    };

    const ensureDataFetched = () => {
        requestBusinessCreditReport(false, creditProvider);
    };

    const { t: tLang, i18n: i18nLang } = useTranslation();

    useEffect(ensureDataFetched, []);

    return (
        <React.Fragment>
            {isLoading ? (
                <div className="box loading"></div>
            ) : (
                <div className="row" id="content">
                    <div className="col-xs col-no-gutter">
                        {creditReportResponses && creditReportResponses.length > 0 && (
                            <div className="row">
                                <div className="col-xs-12 col-sm-8">
                                    <div className="box offset-padding">
                                        <div className="box-header">
                                            <div className="box-title-container">
                                                <div className="box-title-icon" aria-hidden="true">
                                                    <RenderIcons iconName={"tachometer-alt"} />
                                                </div>
                                                <div className="box-title-text">
                                                    <h2 className="box-title">{tLang("dashboard.creditScore.title")}</h2>
                                                </div>
                                            </div>
                                            <div className="box-description">
                                                <p>
                                                    Your business credit score is calculated on the information in your business credit report. Each time you apply
                                                    for a loan - whether it is a new credit card, a business loan or a car loan - the company will look at your
                                                    credit score. If your business credit score is too low, you may be denied credit. If you have a high business
                                                    credit score, you will receive a higher credit line, be allowed to borrow more money and/or have a lower
                                                    interest rate.
                                                </p>
                                            </div>
                                            <div className="box-actions"></div>
                                        </div>
                                        <div className="box-content">
                                            <div className="row between-xs">
                                                <div className="col-xs col-gutter-vertical flex-grow-null">
                                                    <ul className="list-reset margin-bottom-s credit-report-dates">
                                                        <li>
                                                            <em className="las la-calendar-day icon-inline"></em>
                                                            <strong>{creditProvider === "CREDITSAFE" ? "Creditsafe" : "Dun & Bradstreet"} Last Refreshed: </strong>
                                                            {creditReport?.CreatedDate && getFormattedDate(i18nService.get("Country"), creditReport?.CreatedDate)}
                                                        </li>
                                                    </ul>
                                                </div>
                                                <div className="col-xs col-gutter-vertical flex-grow-null">
                                                    <button type="button" className="btn-default btn-primary" onClick={() => orderNewBusinesCreditReport()}>
                                                        <span className="btn-text">Refresh Credit Report</span>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="line-divider margin-vertical-l"></div>

                                            <ul className="credit-bureau-list">
                                                <li>
                                                    <div className="m-credit-bureau-container">
                                                        <div
                                                            className={`credit-bureau-score ${getRatingColor(
                                                                props.reportObject.businessCompanyReport?.credit_score_and_limit?.current_score
                                                            )}`}
                                                        >
                                                            <div className="credit-bureau-score-value">
                                                                {getScore(props.reportObject.businessCompanyReport?.credit_score_and_limit?.current_score)}
                                                            </div>
                                                            <div className="credit-bureau-score-rating">
                                                                {creditProvider === "DUNBRADSTREET"
                                                                    ? getRatingBadgeText(
                                                                          props.reportObject.businessCompanyReport?.credit_score_and_limit?.current_score
                                                                      )
                                                                    : props.reportObject.businessCompanyReport?.credit_score_and_limit?.current_risk_rating}
                                                            </div>
                                                        </div>
                                                        <div className={getCreditBureauTextCssClass()}>
                                                            <h3 className="credit-bureau-title" id="cb-title-creditsafe">
                                                                {creditProvider === "CREDITSAFE" ? "Creditsafe" : "Dun & Bradstreet"}
                                                                <span className="sr-only">{tLang("dashboard.creditScore.title")}</span>
                                                            </h3>
                                                            <p>
                                                                <small>
                                                                    <strong>
                                                                        {creditReport?.CreatedDate &&
                                                                            "Updated " + getFormattedDate(i18nService.get("Country"), creditReport?.CreatedDate)}
                                                                    </strong>
                                                                </small>
                                                            </p>
                                                            <div className="spacer"></div>
                                                            <div className="msg-generic msg-small msg-no-background">
                                                                <em className="las la-info-circle"></em>
                                                                {getScoreDescription(
                                                                    props.reportObject.businessCompanyReport?.credit_score_and_limit?.current_score
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>

                                            <div className="space-divider margin-top-l"></div>

                                            <button
                                                aria-expanded="false"
                                                className="btn-default btn-toggle btn-no-padding"
                                                id="btn_more_score_factors"
                                                onClick={(e) => handleMoreScoreFactorDialog("show")}
                                            >
                                                <span className="btn-toggle-status sr-only">Show</span>
                                                <span className="btn-text">Find out more about Score Factors</span>
                                            </button>
                                        </div>
                                    </div>

                                    {creditProvider === "CREDITSAFE" ? (
                                        <BCreditScoreTracker reportObject={props.reportObject} showDetails={true} />
                                    ) : (
                                        <BCreditRiskTracker reportObject={props.reportObject} showDetails={true} view={"score"} />
                                    )}
                                </div>
                                <div className="col-xs-12 col-sm-4">
                                    <DedicatedResolutionSpecialistCard />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {showMoreScoreFactorDialog && (
                <DialogComponent dialogId="dialog-transaction-delete-account" title="Delete Account" closeDialog={() => handleMoreScoreFactorDialog("close")}>
                    <MoreScoreFactorDialog reportObject={props.reportObject} closeDialog={() => handleMoreScoreFactorDialog("close")}></MoreScoreFactorDialog>
                </DialogComponent>
            )}
        </React.Fragment>
    );
};

export default BCreditScore;
